import {
  isSelfPayBenefit,
  requiresAbnSignature,
} from "constants/benefitSelection";
import { orderTypeConstants } from "constants/order";

export interface IAbnSelectionProcedure {
  cptCode: string;
  cptDescription: string;
  amount: number;
}

/**
 * Procedures the patient pays for out of pocket, grouped by CPT code.
 *
 * Used for the "not covered" callouts on checkout, the order view and the estimate PDF.
 * Includes both self-pay selections — plain SELF_PAY and SELF_PAY_WITH_ABN (CLE-2659) —
 * since they price identically.
 */
export function getNonCoveredProcedures(order: any): any[] {
  if (!order || order.orderType !== orderTypeConstants.GFE) return [];

  const list =
    order?.serviceDetails?.procedures?.filter((procedure: any) =>
      isSelfPayBenefit(procedure?.benefitsForProvProc?.benefitSelectionType)
    ) || [];

  const groupedByCode: Record<string, any> = list.reduce(
    (acc: Record<string, any>, item: any) => {
      const code = item?.code || "UNKNOWN";
      const amount = item?.amountInCents || 0;
      if (!acc[code]) {
        acc[code] = { ...item, amountInCents: amount };
      } else {
        acc[code].amountInCents = (acc[code].amountInCents || 0) + amount;
      }
      return acc;
    },
    {}
  );

  return Object.values(groupedByCode);
}

/**
 * The CPT codes an ABN must cover, with the patient's estimated cost for each.
 *
 * A procedure row requires an ABN when its benefit selection is REQUIRES_ABN or
 * SELF_PAY_WITH_ABN. The estimated cost is the sum of `patientPortion` for exactly
 * the specialty rows that carry that selection — a code flagged only on the facility
 * contributes only the facility's portion.
 *
 * CLE-2659: this used to require a PRIMARY_PROVIDER row before any ABN would render
 * (commit b8c60089), which meant facility-only orders — imaging, for instance — could
 * never collect one, and anesthesia/pathology amounts were dropped from the total even
 * when flagged. Both restrictions are gone.
 *
 * Returns null when nothing on the order requires an ABN.
 */
export function getAbnSelectionProcedures(
  order: any
): IAbnSelectionProcedure[] | null {
  if (!order || order.orderType !== orderTypeConstants.GFE) return null;

  const allProcedures = order?.serviceDetails?.procedures || [];

  // Which specialties require an ABN, per CPT code.
  const abnSpecialtiesByCode: Record<string, Set<string>> = {};

  allProcedures.forEach((procedure: any) => {
    if (
      !requiresAbnSignature(
        procedure?.benefitsForProvProc?.benefitSelectionType
      ) ||
      !procedure?.specialty
    ) {
      return;
    }
    const { code, specialty } = procedure;
    if (!abnSpecialtiesByCode[code]) {
      abnSpecialtiesByCode[code] = new Set();
    }
    abnSpecialtiesByCode[code].add(specialty);
  });

  if (Object.keys(abnSpecialtiesByCode).length === 0) return null;

  const grouped: Record<string, IAbnSelectionProcedure> = {};

  order.paymentFullBreakdownByCPTCode?.bundledProviderAmounts?.forEach(
    (item: any) => {
      const code = item?.cptCode;
      if (!abnSpecialtiesByCode[code]?.has(item?.specialty)) return;

      if (!grouped[code]) {
        grouped[code] = {
          cptCode: code,
          cptDescription: item?.cptDescription,
          amount: 0,
        };
      }
      grouped[code].amount += item?.patientPortion || 0;
    }
  );

  return Object.values(grouped);
}
